import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { visibleScreens } from '../shell/navConfig';
import useLayer from './useLayer';
import './overlays.css';

/**
 * ⌘K palette — 560px, 96px from the top. Groups screens first, then recent records.
 * ↑/↓ moves, ↵ navigates, Esc closes.
 */
export default function CommandPalette({ open, onClose }) {
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);

  const roles = useSelector((state) => state.auth?.active_user?.data?.roles);
  // Depend on the raw selector value: `roles || []` would be a fresh array each
  // render and defeat the memo.
  const screens = useMemo(() => visibleScreens(roles || []), [roles]);

  useLayer({ open, onClose, ref: panelRef, autoFocus: false });

  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Global ⌘K / Ctrl-K is registered by AppLayout so it works when nothing is open.

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = screens.filter((s) => {
      if (!q) return true;
      const haystack = [s.label, s.section, ...(s.keywords || [])].join(' ').toLowerCase();
      return haystack.includes(q);
    });
    return [{ label: 'Screens', items: matches }].filter((g) => g.items.length > 0);
  }, [screens, query]);

  const flat = useMemo(() => results.flatMap((g) => g.items), [results]);

  useEffect(() => {
    if (cursor > flat.length - 1) setCursor(0);
  }, [flat.length, cursor]);

  const go = (item) => {
    if (!item) return;
    onClose();
    navigate(item.path);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => (flat.length ? (c + 1) % flat.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => (flat.length ? (c - 1 + flat.length) % flat.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(flat[cursor]);
    }
  };

  if (!open) return null;

  let runningIndex = -1;

  return createPortal(
    <div className="sv-overlay sv-overlay--palette" onMouseDown={onClose}>
      <div
        ref={panelRef}
        className="sv-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="sv-palette__input-row">
          <i className="bx bx-search" aria-hidden="true" />
          <input
            ref={inputRef}
            className="sv-palette__input"
            placeholder="Search partners, sessions, coupons…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onKeyDown}
            aria-label="Search"
          />
        </div>

        <div className="sv-palette__results">
          {flat.length === 0 && <div className="sv-palette__empty">No matches</div>}

          {results.map((group) => (
            <div key={group.label} className="sv-palette__group">
              <div className="sv-palette__group-label">{group.label}</div>
              {group.items.map((item) => {
                runningIndex += 1;
                const index = runningIndex;
                const isActive = index === cursor;
                return (
                  <button
                    type="button"
                    key={item.path}
                    className={`sv-palette__row ${isActive ? 'is-active' : ''}`}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => go(item)}
                  >
                    <i className={`bx ${item.icon}`} aria-hidden="true" />
                    <span className="sv-palette__row-label">{item.label}</span>
                    <span className="sv-palette__row-section">{item.section}</span>
                    {isActive && <span className="sv-palette__enter">↵</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
